
import { Component, Prop, Vue } from 'vue-property-decorator';
import { debounce } from 'vue-debounce';
import MaterialImageCarousel from '@/components/views/project/MaterialImageCarousel.vue';
import ProductCategoryAPI from '@/api/ProductCategoryAPI';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { PopupEventData } from '@/interfaces/components/PopupEventData';
import { PopupEvents } from '@/enums/global/PopupEvents';
import { filterProductsFromOfferItems } from '@/helpers/OfferItemTableHelper';
import OfferItem from '@/models/OfferItem';
import OfferTitle from '@/models/OfferTitle';
import ProductCatalogue from '@/models/ProductCatalogue';
import ProjectRepository from '@/repositories/ProjectRepository';
import { stripNaturalWords} from '@/helpers/MaterialHelper';

interface ConsolidatedGroup {
  key: string;
  nameKey: string;
  icon: string;
}

interface Pagination {
  page: number;
  perPage: number;
  total: number;
  lastPage: number;
}

interface GroupMaterial {
  id: string;
  oid: string;
  description: string;
  commodityGroup: string;
  examplePriceVK1: number;
  examplePriceVK2: number;
  unit: string | null;
  articleNumber: string | null;
  productType: number;
  perUnit: number;
}

const CONSOLIDATED_GROUPS: ConsolidatedGroup[] = [
  { key: 'CEVI_IN_KOTNIKI', nameKey: 'Cevi in kotniki', icon: 'cevi_in_kotniki' },
  { key: 'NADOKONSKE_ROLETE',nameKey: 'Nadokonske rolete', icon: 'nadokenske_rolete' },
  { key: 'PREDOKONSKE_ROLETE', nameKey: 'Predokonske rolete', icon: 'predokenske_rolete' },
  { key: 'ZALUZIJE', nameKey: 'Žaluzije', icon: 'zaluzije' },
  { key: 'KOMARNIKI', nameKey: 'Komarniki', icon: 'komarniki' },
  { key: 'SCREEN', nameKey: 'Screen', icon: 'screen' },
  { key: 'VRATA', nameKey: 'Vrata', icon: 'vrata' },
  { key: 'ELEKTRIKA', nameKey: 'Elektronika', icon: 'elektronika' },
  { key: 'OSTALO', nameKey: 'Ostalo', icon: 'ostalo' },
];

// Alternatives to try if 'appstore' doesn't feel right:
// 'box' | 'container' | 'gold' | 'inbox' | 'block' | 'partition' | 'cluster' | 'apartment'
const GROUP_ICON = 'appstore';

const DEFAULT_PAGINATION: Pagination = Object.freeze({ page: 1, perPage: 10, total: 0, lastPage: 1 });

@Component({
  name: 'SearchMaterials',
  components: { MaterialImageCarousel },
  filters: {
    moneyFormat(value: any): string {
      if (typeof value !== 'number') {
        const parsed = parseFloat(value);
        if (isNaN(parsed)) return '0,00 €';
        value = parsed;
      }
      return new Intl.NumberFormat('sl-SI', { style: 'currency', currency: 'EUR' }).format(value);
    },
  },
})
export default class SearchMaterials extends Vue {
  @Prop({ required: true }) public projectId!: string;
  @Prop({ default: false }) public isNewDesign!: boolean;
  @Prop({ default: null }) public offerId!: string | null;
  @Prop({ default: null }) public clientsPaymentTypeId!: string | null;
  @Prop({ required: true }) private offerItems!: Array<OfferItem | OfferTitle>;
  @Prop({ default: false }) public isCalculator!: boolean;

  private materialName = '';
  private searchQuery = '';
  private isSearching = false;
  private isDropdownVisible = false;
  private selectedMaterialId = '';
  private searchableMaterials: Record<string, { group: string; children: any[] }> = {};
  private availableMaterials: any[] = [];
  private selectedGroupKey = '';
  private groupMaterials: GroupMaterial[] = [];
  private innerGroupSearchQuery = '';
  private loadingGroupMaterials = false;
  private pagination: Pagination = { ...DEFAULT_PAGINATION };
  private consolidatedGroups = CONSOLIDATED_GROUPS;
  private debouncedInnerSearch!: (val: string) => void;
  private debouncedMaterialSearch!: (val: string) => void;

  created() {
    this.debouncedInnerSearch = debounce((val: string) => {
      this.innerGroupSearchQuery = val;
      this.fetchGroupMaterials(1);
    }, 350);
    this.debouncedMaterialSearch = debounce((val: string) => {
      this.onMaterialsSearch(val);
    }, 400);
  }

  private get currentUserGroup() {
    return UserGroupRepository.getById(String(this.$store.getters['jwtData/currentUserGroup']));
  }

  private get translatedGroups() {
    return CONSOLIDATED_GROUPS.map(group => ({ ...group, name: this.$t(group.nameKey) as string }));
  }

  private get clientsPriceTypeId(): string | null {
      const project = ProjectRepository.getProjectById(this.projectId);
      return project?.client?.priceType?.id ?? null;
  }

  private get currentGroupName(): string {
    const activeGroup = CONSOLIDATED_GROUPS.find((g) => g.key === this.selectedGroupKey);
    return activeGroup ? (this.$t(activeGroup.nameKey) as string) : '';
  }

  private get selectedMaterial(): any | null {
    if (!this.selectedMaterialId) return null;
    const material = (
      this.availableMaterials.find((m) => m.id === this.selectedMaterialId) ||
      this.groupMaterials.find((m) => m.id === this.selectedMaterialId) ||
      null
    );
    if (!material) return null;
    return material;
  }

  private priceFor(material: any): number {
      const priceTypeId = this.isCalculator || !this.clientsPriceTypeId
          ? this.currentUserGroup?.clientPriceTypeId
          : this.clientsPriceTypeId;

      return priceTypeId == '1'
          ? material.examplePriceVK1
          : material.examplePriceVK2;
  }

  private formatName(value: string): string {
    return stripNaturalWords(value);
  }

  private async selectGroup(groupKey: string): Promise<void> {
    this.selectedGroupKey = groupKey;
    this.innerGroupSearchQuery = '';
    this.selectedMaterialId = '';
    this.pagination = { ...DEFAULT_PAGINATION };
    await this.fetchGroupMaterials(1);
  }

  private clearGroup(): void {
    this.selectedGroupKey = '';
    this.groupMaterials = [];
    this.innerGroupSearchQuery = '';
    this.selectedMaterialId = '';
    this.pagination = { ...DEFAULT_PAGINATION };
  }

  private async fetchGroupMaterials(page: number): Promise<void> {
    this.loadingGroupMaterials = true;
    try {
      const response = await ProductCategoryAPI.getMaterialsByGroup(this.selectedGroupKey, {
        page,
        perPage: this.pagination.perPage,
        search: this.innerGroupSearchQuery.trim(),
      });
      const { data, meta } = response.data;
      this.groupMaterials = (data ?? []).map((el: any) => ({ ...el, gallery: el.gallery ? [...el.gallery] : [] })) as unknown as GroupMaterial[];
      this.pagination = {
        page: meta.page,
        perPage: meta.per_page,
        total: meta.total,
        lastPage: meta.last_page,
      };
    } catch {
      this.$notification.error({
        key: 'err-group-materials',
        message: this.$t('Greška') as string,
        description: this.$t('Nije moguće dohvatiti materijale grupe') as string,
      });
    } finally {
      this.loadingGroupMaterials = false;
    }
  }

  private onInnerSearchChange(val: string): void {
    this.selectedMaterialId = '';
    this.debouncedInnerSearch(val);
  }

  private onPageChange(page: number): void {
    this.fetchGroupMaterials(page);
  }

  private onPageSizeChange(current: number, size: number): void {
    this.pagination.perPage = size;
    this.fetchGroupMaterials(1);
  }

  private async onMaterialsSearch(val: string): Promise<void> {
    const searchString = val.trim();
    if (searchString.length < 3) {
      this.selectedMaterialId = '';
      this.searchableMaterials = {};
      this.availableMaterials = [];
      this.isDropdownVisible = false;
      return;
    }
    this.isSearching = true;
    const rawResults = await ProductCatalogue.searchOnlyMaterials(searchString);

    if (searchString !== this.materialName.trim()) {
      this.isSearching = false;
      return;
    }

    const groupedMap: Record<string, { group: string; children: any[] }> = {};
    const flatList: any[] = [];

    for (let i = 0; i < rawResults.length; i++) {
      const el = rawResults[i];
      const optimizedEl = Object.freeze({
        id: el.id,
        oid: el.oid,
        description: el.description,
        commodityGroup: el.commodityGroup,
        examplePriceVK1: el.examplePriceVK1,
        examplePriceVK2: el.examplePriceVK2,
        unit: el.unit,
        articleNumber: el.articleNumber,
        gallery: el.gallery ? [...el.gallery] : [],
      });
      flatList.push(optimizedEl);
      const g = optimizedEl.commodityGroup || 'Ostalo';
      if (!groupedMap[g]) {
        groupedMap[g] = { group: g, children: [optimizedEl] };
      } else {
        groupedMap[g].children.push(optimizedEl);
      }
    }
    this.searchableMaterials = groupedMap;
    this.availableMaterials = flatList;
    this.isDropdownVisible = flatList.length > 0;
    this.isSearching = false;
  }

  private onSelect(id: string): void {
    this.selectedMaterialId = id;
  }

  private async dispatchDirectMaterial(id: string): Promise<void> {
    const material = this.groupMaterials.find((m) => m.id === id);
    if (material) {
      await ProductCatalogue.insertOrUpdate({ data: [material] });
    }
    this.selectedMaterialId = id;
    this.onSubmit();
  }

  private onSubmit(fromMainSearch = false): void {
    if (!this.selectedMaterialId) return;
    this.isDropdownVisible = false;
    const popupData: PopupEventData = {
      popupEvent: PopupEvents.openAddMaterials,
      data: {
        materialId: this.selectedMaterialId,
        projectId: this.projectId,
        offerId: this.offerId,
        clientsPaymentTypeId: this.clientsPaymentTypeId,
        offerItems: filterProductsFromOfferItems(this.offerItems, false),
        isCalculator: this.isCalculator,
        fromMainSearch,
      },
    };
    EventBus.$emit(EventBusEvents.openMaterialsPopup, popupData);
    this.selectedMaterialId = '';
    this.materialName = '';
    this.searchableMaterials = {};
    this.availableMaterials = [];
  }

  private onPressEnter(): void {
    if (this.selectedMaterialId) this.onSubmit();
  }
}
